import type { DqGlobalProps } from "#/components/Question/types";
import { useGetQuestions } from "#/components/Question/utils";
import { ErrorReport } from "#/constants";
import { postErrorReport } from "#/services/rest/gwt/exam-results/error-report/error-report.api";
import { errorToastOption } from "@monta/components";
import {
  BottomSheet,
  Button,
  TextField,
  Toast,
  Tooltip,
  useBottomSheet,
  useBreakLG,
  useTooltip,
} from "@monta/design-system";
import { DangerColorful, DangerTriangle } from "@monta/icons";
import clsx from "clsx";
import { useState } from "react";
import { useSelectedStudentIdContext } from "../../context/studentsList/context";
import styles from "./errorReport.module.scss";

const errorReportItems = [
  { label: "اشتباه نگارشی", value: ErrorReport.WRITING_MISTAKE },
  { label: "اشتباه علمی", value: ErrorReport.SCIENTIFIC_MISTAKE },
  { label: "پاسخ نامفهوم", value: ErrorReport.UNCLEAR_ANSWER },
  { label: "گزینه اشتباه", value: ErrorReport.WRONG_CHOICE },
  { label: "موضوع سوال", value: ErrorReport.WRONG_TOPIC },
  { label: "سطح دشواری", value: ErrorReport.WRONG_DIFFICULTY_LEVEL },
  { label: "سوال تکراری", value: ErrorReport.REPETITIVE_QUESTION },
  { label: "سوال بی کیفیت", value: ErrorReport.WITHOUT_QUALITY_QUESTION },
];

const ErrorReportPopOver = (props: DqGlobalProps) => {
  const [value, setValue] = useState("");
  const [selectedTagId, setSelectedTagId] = useState<ErrorReport[]>([]);
  const errorReportController = useBottomSheet();
  const isLG = useBreakLG();
  const tooltipController = useTooltip();
  const { selectedStudentId } = useSelectedStudentIdContext();

  const { id } = useGetQuestions(selectedStudentId, props.id)[0];

  const { mutate, isLoading } = postErrorReport.useMutation({
    onSuccess: (result) => {
      if (result.resultCode > 0)
        Toast.success({
          message: "گزارش خطای شما با موفقیت ثبت شد",
        });
      else if (result.resultCode === -40)
        Toast.info({ message: "گزارش شما در حال بررسی است." });
      else Toast.error(errorToastOption);
      setSelectedTagId([]);
      setValue("");
      tooltipController.close();
      errorReportController.close();
    },
    onError: () => {
      Toast.error(errorToastOption);
    },
  });

  const tagsOnChange = (isSelected: boolean, id: ErrorReport) => {
    const tagList = selectedTagId.filter((item) => item != id);
    if (isSelected) setSelectedTagId(tagList);
    else setSelectedTagId(tagList.concat([id]));
  };

  const content = (
    <div className={clsx(styles["error-report"])}>
      {!isLG ? (
        <div className={clsx(styles["error-report__title"])}>
          <DangerColorful />
          <span>گزارش خطا</span>
        </div>
      ) : (
        <div className={clsx(styles["error-report__title"])}>
          گزارش ایراد در سوال
        </div>
      )}
      <div className={clsx(styles["error-report__items"])}>
        {errorReportItems.map((item, index) => {
          const isSelected = Boolean(
            selectedTagId.find((id) => id == item.value),
          );
          return (
            <div
              onClick={() => {
                tagsOnChange(isSelected, item.value);
              }}
              key={index + "error-report"}
              className={clsx(
                styles["error-report__items__item"],
                isSelected && styles["error-report__items__item--active"],
              )}
            >
              {item.label}
            </div>
          );
        })}
      </div>
      <div className={styles["error-report__input-container"]}>
        <TextField
          nodetail
          className={clsx(styles["error-report__input-field"])}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={80}
          placeholder="دلیل خود را بنویسید..."
        />
      </div>
      <div className={clsx(styles["error-report__buttons"])}>
        <Button
          borderRadius="4"
          height={58}
          color="#475467"
          width={94}
          onClick={() => {
            setSelectedTagId([]);
            setValue("");
            errorReportController.close();
            tooltipController.close();
          }}
        >
          انصراف
        </Button>
        <Button
          borderRadius="4"
          color="#fff"
          bgColor="primary-600"
          height={58}
          width={186}
          loading={isLoading}
          onClick={() => {
            if (selectedTagId.length > 0)
              mutate({
                body: {
                  bugIds: selectedTagId,
                  ...(value && { description: value }),
                  questionId: Number(id),
                },
              });
            else Toast.error({ message: "دلیل خود را انتخاب کنید" });
          }}
        >
          ثبت گزارش
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {isLG ? (
        <Tooltip
          tipClassName={styles["tooltip-container"]}
          trigger="manual"
          text={content}
          isOpen={tooltipController.isOpen}
          position="right"
        >
          <div
            className={styles["report-error-container"]}
            onClick={() => {
              if (tooltipController.isOpen) {
                tooltipController.close();
              } else {
                tooltipController.open();
              }
            }}
          >
            <span>گزارش خطا</span>
            <DangerTriangle size="small" color="#fff" />
          </div>
        </Tooltip>
      ) : (
        <div
          className={styles["report-error-container"]}
          onClick={() => {
            if (tooltipController.isOpen) {
              tooltipController.close();
            } else {
              tooltipController.open();
            }
          }}
          style={{ padding: "4px" }}
        >
          <DangerTriangle
            onClick={() => {
              errorReportController.open();
            }}
            size="small"
            color="#fff"
          />
        </div>
      )}
      <BottomSheet {...errorReportController} borderRadius="24">
        {content}
      </BottomSheet>
    </>
  );
};

export default ErrorReportPopOver;
